import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import stepConnectorAsset from "@/assets/connect_step_connector.png.asset.json";
import stepAddModalAsset from "@/assets/connect_step_add_modal.png.asset.json";
import stepConsentAsset from "@/assets/connect_step_consent.png.asset.json";
import stepResultsAsset from "@/assets/connect_step_results_top4.png.asset.json";
import stepContactsAsset from "@/assets/connect_step_contacts_two.png.asset.json";
import { MobileNav } from "@/components/marketing/mobile-nav";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const GOLD_2 = "#D8B260";
const INK = "#14213A";
const INK_2 = "#545C6B";
const INK_MUTED = "#6B7384";
const LINE = "#E4DECF";
const NAVY_TEXT = "#EDEAE0";
const NAVY_TEXT_DIM = "#B6BFCD";
const NAVY_TEXT_DIM_2 = "#9AA5B4";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

const MCP_URL = "https://capitaldart.com/mcp";

const TITLE = "Use CapitalDart in Claude | Find investors from your AI assistant.";
const DESCRIPTION =
  "Connect CapitalDart to Claude as a custom connector. Describe your raise, get ranked investor firms and verified contacts from CapitalDart's proprietary directory, and draft outreach you send from your own accounts.";

export const Route = createFileRoute("/connect_/claude")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://capitaldart.com/connect/claude" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/connect/claude" }],
  }),
  component: ConnectPage,
});

function ConnectPage() {
  return (
    <main
      id="top"
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased overflow-x-clip"
    >
      <style>{`
        @media (max-width: 1023.98px) { .cd-connect-nav-mobile { display: inline-flex !important; } }
        .cd-copy-input:focus { outline: 2px solid ${GOLD}; outline-offset: 2px; }
      `}</style>
      <Hero />
      <Steps />
      <WhatYouCanAsk />
      <WhatToExpect />
      <CreditsAccount />
      <FinalCta />
      <LandingFooter />
    </main>
  );
}

function LandingLogo({ height = 32 }: { height?: number }) {
  return (
    <Logo
      src={LOGO_LIGHT}
      alt="CapitalDart"
      height={height}
      style={{ display: "block" }}
      trademarkClassName="text-[#F4F1E8]"
    />
  );
}

function GoldButton({
  children,
  to,
  href,
  search,
  style,
}: {
  children: React.ReactNode;
  to?: "/auth" | "/dashboard";
  href?: string;
  search?: { mode: "signin" | "signup" };
  style?: React.CSSProperties;
}) {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: FONT_SANS,
    fontWeight: 600,
    color: NAVY,
    background: GOLD,
    borderRadius: 2,
    letterSpacing: ".01em",
    textDecoration: "none",
    transition: "background .15s",
    ...style,
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD_2;
  };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD;
  };
  if (to && search) {
    return (
      <Link to={to} search={search} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        {children}
      </Link>
    );
  }
  if (to) {
    return (
      <Link to={to} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
    </a>
  );
}

function Kicker({
  children,
  color = INK_MUTED,
  style,
}: {
  children: React.ReactNode;
  color?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: 12,
        letterSpacing: ".2em",
        textTransform: "uppercase",
        color,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Hero() {
  const { session, loading } = useAuth();
  const isAuthed = Boolean(session);
  return (
    <section
      style={{
        background: NAVY,
        backgroundImage: `radial-gradient(120% 80% at 85% -10%, rgba(201,162,75,.12), rgba(11,37,69,0) 60%)`,
        color: NAVY_TEXT,
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)",
          backgroundSize: "64px 64px",
          pointerEvents: "none",
        }}
      />
      <nav
        className="mx-auto"
        style={{
          maxWidth: 1200,
          position: "relative",
          padding: "26px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        <Link to="/" hash="top" style={{ textDecoration: "none" }}>
          <LandingLogo height={34} />
        </Link>
        <div className="hidden lg:flex" style={{ alignItems: "center", gap: 18 }}>
          {loading ? null : isAuthed ? (
            <GoldButton to="/dashboard" style={{ fontSize: 14.5, padding: "11px 20px" }}>
              Dashboard
            </GoldButton>
          ) : (
            <>
              <Link
                to="/auth"
                search={{ mode: "signin" }}
                style={{
                  color: "#AEB8C7",
                  fontSize: 14.5,
                  fontFamily: FONT_SANS,
                  textDecoration: "none",
                }}
              >
                Sign in
              </Link>
              <GoldButton
                to="/auth"
                search={{ mode: "signup" }}
                style={{ fontSize: 14.5, padding: "11px 20px" }}
              >
                Get started
              </GoldButton>
            </>
          )}
        </div>
        <div className="lg:hidden">
          <MobileNav className="cd-connect-nav-mobile" />
        </div>
      </nav>

      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ maxWidth: 1200, position: "relative", padding: "80px 0 110px" }}
      >
        <div className="max-w-3xl">
          <Kicker color={GOLD} style={{ marginBottom: 22 }}>
            CapitalDart for Claude
          </Kicker>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(40px,5vw,62px)",
              lineHeight: 1.04,
              letterSpacing: "-.015em",
              margin: "0 0 20px",
              color: "#F7F5EE",
            }}
          >
            Use CapitalDart in Claude.
          </h1>
          <p
            style={{
              fontSize: 18,
              lineHeight: 1.6,
              color: NAVY_TEXT_DIM,
              maxWidth: 680,
              margin: "0 0 40px",
            }}
          >
            Connect once, then ask Claude to find investors, get verified contacts, and draft
            outreach, powered by CapitalDart's proprietary directory. Your deals, pipeline
            tracking, and Investor Readiness Reports live on capitaldart.com.
          </p>
        </div>
        <div style={{ marginTop: 40, textAlign: "center" }}>
          <Link
            to="/connect/capabilities"
            style={{
              fontFamily: FONT_SANS,
              fontSize: 15,
              color: INK,
              textDecoration: `underline ${GOLD}`,
              textUnderlineOffset: 4,
            }}
          >
            Read the full capabilities reference
          </Link>
          <div style={{ marginTop: 14 }}>
            <Link
              to="/connect/chatgpt"
              style={{
                fontFamily: FONT_SANS,
                fontSize: 14.5,
                color: NAVY_TEXT_DIM,
                textDecoration: `underline ${GOLD}`,
                textUnderlineOffset: 4,
              }}
            >
              Setting up ChatGPT instead
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

type StepImage = { caption: string; src?: string; alt?: string };

function ImageSlot({ images }: { images: StepImage[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, margin: "20px 0 0" }}>
      {images.map((img, idx) => (
        <figure
          key={idx}
          style={{
            margin: 0,
            border: `1px solid ${LINE}`,
            borderRadius: 6,
            background: "#FFFDF7",
            overflow: "hidden",
          }}
        >
          {img.src ? (
            <img
              src={img.src}
              alt={img.alt ?? img.caption}
              loading="lazy"
              decoding="async"
              style={{ display: "block", width: "100%", height: "auto" }}
            />
          ) : (
            <div
              style={{
                padding: "56px 20px",
                textAlign: "center",
                color: INK_MUTED,
                fontFamily: FONT_MONO,
                fontSize: 12,
                letterSpacing: ".15em",
                textTransform: "uppercase",
                borderStyle: "dashed",
              }}
            >
              Screenshot: {img.caption}
            </div>
          )}
        </figure>
      ))}
    </div>
  );
}

function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  }
  return (
    <div
      style={{
        marginTop: 16,
        display: "flex",
        alignItems: "stretch",
        border: `1px solid ${LINE}`,
        borderRadius: 4,
        background: PAPER_2,
        maxWidth: 560,
      }}
    >
      <input
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className="cd-copy-input"
        style={{
          flex: 1,
          padding: "12px 14px",
          fontFamily: FONT_MONO,
          fontSize: 14,
          background: "transparent",
          border: "none",
          color: INK,
        }}
      />
      <button
        type="button"
        onClick={copy}
        style={{
          padding: "0 18px",
          fontFamily: FONT_SANS,
          fontSize: 13,
          fontWeight: 600,
          color: NAVY,
          background: GOLD,
          border: "none",
          cursor: "pointer",
          letterSpacing: ".02em",
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

const STEPS: Array<{
  n: string;
  title: string;
  body: React.ReactNode;
  images: StepImage[];
}> = [
  {
    n: "01",
    title: "Open Claude Connectors",
    body: (
      <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
        In Claude, click your initials in the lower left corner, choose Settings, then Connectors, and click Add and select Custom. On the mobile app, Settings is under the menu.
      </p>
    ),
    images: [
      {
        caption: "Claude Settings > Connectors > Add custom connector",
        src: stepConnectorAsset.url,
        alt: "Claude Connectors screen showing the CapitalDart custom connector with the https://capitaldart.com/mcp URL and a Connect button.",
      },
    ],
  },
  {
    n: "02",
    title: "Name it CapitalDart and paste the link",
    body: (
      <>
        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
          Name it CapitalDart and paste this link into the connector URL field.
        </p>
        <CopyField value={MCP_URL} />
      </>
    ),
    images: [
      {
        caption: "Add custom connector modal, filled in",
        src: stepAddModalAsset.url,
        alt: "Add custom connector dialog with CapitalDart name and MCP link filled in.",
      },
    ],
  },
  {
    n: "03",
    title: "Sign in and approve",
    body: (
      <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
        Click Connect, sign in with your CapitalDart account or create one, and click Approve on
        the CapitalDart consent screen.
      </p>
    ),
    images: [
      {
        caption: "CapitalDart consent card",
        src: stepConsentAsset.url,
        alt: "CapitalDart OAuth consent card titled Connect Claude to CapitalDart, listing what is being granted and showing Cancel connection and Approve buttons.",
      },
    ],
  },
  {
    n: "04",
    title: "Start asking",
    body: (
      <>
        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
          In a new Claude conversation, try:
        </p>
        <blockquote
          style={{
            margin: "18px 0 0",
            padding: "18px 22px",
            borderLeft: `3px solid ${GOLD}`,
            background: PAPER_2,
            fontFamily: FONT_SERIF,
            fontSize: 18,
            lineHeight: 1.5,
            color: INK,
            fontStyle: "italic",
          }}
        >
          "I'm raising $10M of equity for a fintech company in New York, targeting family
          offices. Find investors for me."
        </blockquote>
      </>
    ),
    images: [
      {
        caption: "Claude conversation with a CapitalDart tool call",
        src: stepResultsAsset.url,
        alt: "Ranked investor matches for a fintech raise showing the top four firms",
      },
      {
        caption: "Delivered contacts in a Claude conversation",
        src: stepContactsAsset.url,
        alt: "Delivered contacts showing name, title, and email for two contacts",
      },
    ],
  },
];

function Steps() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 900 }}>
        <Kicker style={{ marginBottom: 18 }}>Setup</Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(30px,3.4vw,42px)",
            lineHeight: 1.08,
            letterSpacing: "-.01em",
            margin: "0 0 48px",
          }}
        >
          Four steps, about two minutes.
        </h2>
        <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {STEPS.map((s, i) => (
            <li
              key={s.n}
              style={{
                padding: "36px 0",
                borderTop: i === 0 ? "none" : `1px solid ${LINE}`,
              }}
            >
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 12,
                  letterSpacing: ".2em",
                  color: GOLD,
                  marginBottom: 10,
                }}
              >
                STEP {s.n}
              </div>
              <h3
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 24,
                  lineHeight: 1.25,
                  margin: "0 0 14px",
                }}
              >
                {s.title}
              </h3>
              {s.body}
              <ImageSlot images={s.images} />
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const CAPABILITIES: Array<{ title: string; body: string }> = [
  {
    title: "Describe a raise",
    body: "Give Claude a plain-language description or upload your deck and get back a structured targeting plan covering sector, check size, structure, and geography.",
  },
  {
    title: "Find investor firms",
    body: "Get ranked institutional investor firms from CapitalDart's proprietary directory, with honest deliverable contact counts per firm. Browsing firms is free.",
  },
  {
    title: "Build and deliver",
    body: "Turn the plan into a deal on your account and start delivery: CapitalDart identifies the right contact at each matched firm and drafts personalized email and LinkedIn outreach in your voice, delivered to your inbox and dashboard.",
  },
  {
    title: "Manage the raise",
    body: "Track delivery progress, fetch your outreach package, log notes, and update each investor's status from chat. Everything stays in sync with your dashboard at capitaldart.com.",
  },
  {
    title: "Edit anytime",
    body: "Update the deal's description, documents, or targeting from chat, and deliver additional contacts whenever you are ready. Previously delivered firms are excluded automatically.",
  },
  {
    title: "Investor Readiness Report",
    body: "Ask Claude for an AI analysis of your deck: a readiness score, the objections investors will raise, and the changes that answer them, with the full report in your dashboard.",
  },
];

function WhatYouCanAsk() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER_2, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1100 }}>
        <Kicker style={{ marginBottom: 18 }}>What you can ask</Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(30px,3.4vw,42px)",
            lineHeight: 1.08,
            letterSpacing: "-.01em",
            margin: "0 0 48px",
            maxWidth: 780,
          }}
        >
          Your entire raise, wired to your CapitalDart account.
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
          }}
        >
          {CAPABILITIES.map((c) => (
            <div
              key={c.title}
              style={{
                background: PAPER,
                border: `1px solid ${LINE}`,
                borderRadius: 5,
                padding: "28px 26px",
              }}
            >
              <h3
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 20,
                  lineHeight: 1.25,
                  margin: "0 0 10px",
                }}
              >
                {c.title}
              </h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: INK_2, margin: 0 }}>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const EXPECT: Array<{ title: string; body: string }> = [
  {
    title: "Approval prompts are normal",
    body: "Claude may show you the details of each request before it invokes a tool. That is normal. You can choose Always allow to skip the prompt for a given tool.",
  },
  {
    title: "Reconnect is safe",
    body: "If a tool ever reports a connection problem, reconnect from Settings, then Connectors, then CapitalDart, and continue. Reconnecting never loses your work.",
  },
  {
    title: "Never charged twice",
    body: "Contacts you have purchased are never charged again, on any device, in any conversation.",
  },
];

function WhatToExpect() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1100 }}>
        <Kicker style={{ marginBottom: 18 }}>What to expect</Kicker>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
          }}
        >
          {EXPECT.map((e) => (
            <div
              key={e.title}
              style={{
                background: PAPER_2,
                border: `1px solid ${LINE}`,
                borderRadius: 5,
                padding: "28px 26px",
              }}
            >
              <h3
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 19,
                  lineHeight: 1.3,
                  margin: "0 0 10px",
                }}
              >
                {e.title}
              </h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.65, color: INK_2, margin: 0 }}>{e.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CreditsAccount() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER_2, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 900 }}>
        <Kicker style={{ marginBottom: 18 }}>Credits and account</Kicker>
        <p
          style={{
            fontFamily: FONT_SERIF,
            fontSize: "clamp(20px,2.2vw,26px)",
            lineHeight: 1.4,
            margin: "0 0 26px",
            color: INK,
          }}
        >
          Your Claude connection uses the same account and credit balance as capitaldart.com. Buy
          credits at{" "}
          <a
            href="https://capitaldart.com/buy-credits"
            style={{ color: INK, textDecoration: `underline ${GOLD}` }}
          >
            capitaldart.com/buy-credits
          </a>{" "}
          and they are available in Claude immediately, no reconnect needed.
        </p>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      style={{ background: NAVY, color: NAVY_TEXT, padding: "96px 0", position: "relative" }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(90% 70% at 50% 120%, rgba(201,162,75,.14), rgba(11,37,69,0) 60%)`,
          pointerEvents: "none",
        }}
      />
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ position: "relative", maxWidth: 900, textAlign: "center" }}
      >
        <Kicker color={GOLD} style={{ marginBottom: 22 }}>
          The full workflow
        </Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(32px,4.2vw,52px)",
            lineHeight: 1.06,
            letterSpacing: "-.015em",
            margin: "0 0 22px",
            color: "#F7F5EE",
          }}
        >
          Ready for the full workflow?
        </h2>
        <p
          style={{
            fontSize: 17,
            lineHeight: 1.6,
            color: NAVY_TEXT_DIM,
            maxWidth: 640,
            margin: "0 auto 40px",
          }}
        >
          Upload your deck at capitaldart.com for matched investors, outreach drafts, an
          Investor Readiness Report, and a pipeline to track it all.
        </p>
        <GoldButton
          to="/auth"
          search={{ mode: "signup" }}
          style={{ fontSize: 16, padding: "16px 34px" }}
        >
          Get started at capitaldart.com
        </GoldButton>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer style={{ background: NAVY_DEEP, color: NAVY_TEXT_DIM_2, padding: "48px 0" }}>
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{
          maxWidth: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LandingLogo height={26} />
          <span style={{ fontSize: 13.5, color: "#56627A", marginLeft: 14, fontFamily: FONT_SANS }}>
            © 2026
          </span>
        </div>
        <div style={{ display: "flex", gap: 26, fontSize: 14, fontFamily: FONT_SANS }}>
          <Link to="/help" style={{ color: "inherit", textDecoration: "none" }}>
            Help
          </Link>
          <Link to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: "inherit", textDecoration: "none" }}>
            Terms
          </Link>
          <Link to="/connect" style={{ color: "inherit", textDecoration: "none" }}>
            Use with Claude / ChatGPT
          </Link>
          <Link to="/connect/capabilities" style={{ color: "inherit", textDecoration: "none" }}>
            Claude / ChatGPT capabilities
          </Link>
          <Link to="/partners" style={{ color: "inherit", textDecoration: "none" }}>
            Partners
          </Link>
          <Link to="/lists" style={{ color: "inherit", textDecoration: "none" }}>
            Top 10 Investor Rankings
          </Link>
          <Link to="/contact" style={{ color: "inherit", textDecoration: "none" }}>
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}